if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.filtersComponent = document.querySelector('facet-filters');
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      this.layoutSwitcher = this.querySelector('.products-toolbar__layout');
      this.mobileLayoutToggle = this.querySelector('[data-products-layout-toggle]');
      this.compareToggle = document.getElementById('compare-switch');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');

      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        this.filterToggle.addEventListener('click', this.toggleFilters.bind(this));
      }

      if (this.layoutSwitcher && this.productsGrid) {
        this.layoutSwitcher.addEventListener('change', this.handleLayoutChange.bind(this));

        if (this.mobileLayoutToggle) {
          this.mobileLayoutToggle.addEventListener('click', this.handleMobileLayoutToggle.bind(this));
        }

        // Restore the shopper's saved preference. On a shopper's first mobile
        // collection visit, default to List view; desktop keeps the section default.
        const bootstrappedLayout = this.productsGrid.dataset.layout;
        const hasBootstrappedLayout = bootstrappedLayout === 'grid' || bootstrappedLayout === 'list';
        const storedLayout = theme.storageUtil.get('products-layout');
        const hasStoredLayout = storedLayout === 'grid' || storedLayout === 'list';
        const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;
        const initialLayout = hasBootstrappedLayout
          ? bootstrappedLayout
          : (hasStoredLayout
            ? storedLayout
            : (isMobileViewport ? 'list' : 'grid'));

        this.handleLayoutChange(null, initialLayout, false);
      }

      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));

        const isChecked = theme.storageUtil.get('compare-toggle', true);
        if (isChecked !== null) {
          this.toggleCompare(null, isChecked, true);
        } else {
          this.toggleCompare(null, this.compareToggle.checked, false);
        }

        setTimeout(() => {
          this.classList.add('transition-ready');
          this.productsGrid.classList.add('transition-ready');
        }, 500);
      } else if (theme.settings.compareToggle === 'none') {
        this.toggleCompare(null, true, false);
      }

      if (!this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    /**
     * Updates the UI to reflect the compare state
     * @param {object} evt - Change event object
     * @param {boolean} checked - Whether compare is enabled or not (optional)
     * @param {boolean} persist - Whether to persist the new state (optional)
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt ? evt.target.checked : checked;
      this.productsGrid.dataset.compare = isChecked;

      const compareButton = document.getElementById('compare-drawer-open');
      if (compareButton) compareButton.classList.toggle('is-out', !isChecked);

      if (!evt && this.compareToggle) this.compareToggle.checked = isChecked;

      if (persist && !Shopify.designMode) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      // Load scripts/styles if not already loaded
      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          const resources = JSON.parse(resourcesTag.textContent);
          resources.forEach((resource) => {
            if (resource.type === 'js') {
              const script = document.createElement('script');
              script.src = resource.path;
              document.head.appendChild(script);
            } else if (resource.type === 'css') {
              const link = document.createElement('link');
              link.href = resource.path;
              link.type = 'text/css';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
          });
          resourcesTag.remove();
        }
      }
    }

    /**
     * Toggles open/closed state of the filters on desktop.
     */
    toggleFilters() {
      this.filterToggle.classList.toggle('is-active');
      this.filtersOpen = this.filterToggle.classList.contains('is-active');
      this.filterToggle.setAttribute('aria-expanded', this.filtersOpen);

      if (theme.mediaMatches.lg) {
        this.filtersColumn.classList.toggle('lg:js-hidden', !this.filtersOpen);
        const productsList = document.querySelector('.main-products-grid__results > .grid');

        productsList.className = this.filtersOpen
          ? productsList.dataset.filtersOpenClasses
          : productsList.dataset.filtersClosedClasses;
      } else {
        this.filtersComponent.open();
      }
    }

    /**
     * Keeps the single mobile layout button in sync with the current layout.
     * The icon always shows the layout that the next tap will activate.
     * @param {string} currentLayout - 'grid' or 'list'.
     */
    updateMobileLayoutToggle(currentLayout) {
      if (!this.mobileLayoutToggle) return;

      const nextLayout = currentLayout === 'list' ? 'grid' : 'list';
      const listIcon = this.mobileLayoutToggle.querySelector('[data-layout-target="list"]');
      const gridIcon = this.mobileLayoutToggle.querySelector('[data-layout-target="grid"]');
      const label = nextLayout === 'list' ? 'Switch to list view' : 'Switch to grid view';

      if (listIcon) listIcon.hidden = nextLayout !== 'list';
      if (gridIcon) gridIcon.hidden = nextLayout !== 'grid';

      this.mobileLayoutToggle.dataset.nextLayout = nextLayout;
      this.mobileLayoutToggle.setAttribute('aria-label', label);
      this.mobileLayoutToggle.setAttribute('title', label);
    }

    /**
     * Changes list/grid view from the single mobile toggle button.
     */
    handleMobileLayoutToggle() {
      const currentLayout = this.productsGrid.dataset.layout || 'grid';
      const nextLayout = currentLayout === 'list' ? 'grid' : 'list';
      this.handleLayoutChange(null, nextLayout);
    }

    /**
     * Handles 'change' events on the layout switcher buttons.
     * @param {object} evt - Event object.
     * @param {string} layout - 'grid' or 'list' - the layout to change to.
     * @param {boolean} persist - Whether to save the preference.
     */
    handleLayoutChange(evt, layout = null, persist = true) {
      if (!this.productsGrid) return;

      const selectedLayout = layout || (evt && evt.target ? evt.target.value : 'grid');
      if (selectedLayout !== 'grid' && selectedLayout !== 'list') return;

      this.productsGrid.dataset.layout = selectedLayout;

      const radio = this.querySelector(`#${selectedLayout}-layout-opt`);
      if (radio) radio.checked = true;

      this.updateMobileLayoutToggle(selectedLayout);

      if (persist && !Shopify.designMode) {
        theme.storageUtil.set('products-layout', selectedLayout);
      }
    }

    /**
     * Handles when a sort by dropdown is changed (and filtering is disabled)
     * @param {object} evt - Event object.
     */
    static handleSortByChange(evt) {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      urlObj.hash = 'products-toolbar';
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
